import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { sendParticipantActivity } from '../../api/instantmessage-control';
import { useSelector } from '../../hook-utils';
import { openSmartSearchDialogTeams } from '../../legacy';
import { EntityType, ExtensionStatus, ExtensionSubStatus, InstantMessagerParticipantEndpointScheme } from '../../model';
import { actions } from '../../store';
import { selectConfigProp, selectMyself } from '../../store/reducers/config';
import { getInstantMessageById } from '../../store/reducers/instantmessager';
import { useMediaScripts } from '../AgentView';
import HtmlEditor, { HtmlEditorApi } from '../HtmlEditor';
import { ReactComponent as AddPeopleIcon } from '../icons/add-people.svg';
import { ReactComponent as IconSave } from '../icons/save.svg';
import './InstantMessagerView.scss';
import { ConversationParticipant } from './ConversationParticipant';
import { EditableText } from '../AgentView/TasksPage';

export function InstantMessagerView({ id }: { id: string }) {
  const mySelf = useSelector(selectMyself)
  const ref = useRef<HTMLDivElement>(null)
  const editorRef = React.useRef<HtmlEditorApi | null>(null)
  const dispatch = useDispatch()
  const selected = useSelector(s => getInstantMessageById(s, id))
  const [text, setText] = useState('')
  const triggered = useRef(false)
  const socketId = useSelector(s => selectConfigProp(s, 'socketId'))
  const saveHtml = () => {
    const conversationHtml = html; // this is assumed to be your inner .messages content

    const fullHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${selected.conversationTitle}</title>
         ${style}
      </head>
      <body>
        <div class="messages">
          ${conversationHtml}
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([fullHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = selected.conversationTitle + '.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const scrollMessages = useCallback((smooth: boolean) => {
    if (ref.current !== null) {
      ref.current.scrollTo({ top: ref.current.scrollHeight, behavior: smooth ? 'smooth' : undefined })
    }
  }, [])

  // on first load scroll to the end without animation
  useEffect(() => {
    scrollMessages(false)
  }, [scrollMessages])

  // on new message scroll with animation
  useEffect(() => {
    scrollMessages(true)
  }, [selected, scrollMessages]);

  useEffect(() => {
    if (selected) {
      if (text !== '' && !triggered.current && socketId) {
        triggered.current = true
        sendParticipantActivity(selected.conversationId, true, socketId)
      } else if (text === '' && triggered.current && socketId) {
        triggered.current = false
        sendParticipantActivity(selected.conversationId, false, socketId)
      } else if (selected.conversationViewOnly !== undefined && selected.conversationViewOnly) {
        setText('')
      }
    }
  }, [selected, socketId, text])

  useMediaScripts(html => {
    const editor = editorRef.current
    if (!editor) { return }

    setText(html)

    window.setTimeout(() => {
      const editor = editorRef.current
      if (editor) { editor.focus() }
    }, 100)

  })

  // HACK: win the focus war with the Notes field
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const editor = editorRef.current
      if (editor) { editor.focus() }
    }, 100)
    return () => window.clearTimeout(timer)
  }, [])

  if (!selected || !mySelf) return <div></div>

  function sendMessage() {
    if (text !== '') {
      dispatch(actions.instantMessagerMessageSend(id, text.replace(/\xa0/g, ' ')))
      setText('')
    }
  }

  //just in case we haven't joined yet should only be on refresh
  if (!selected.joined) {
    dispatch(actions.changeInstantmessageView(selected.conversationId))
  }

  let html = ''
  selected.messages && selected.messages.forEach(m => {
    html += m.message
  })

  return <div className='InstantMessagerView'>
     <div className='row'>
      <EditableText type='input'
        className='SidebarViewHeader imHeader'
        iconClassName='editIcon icon'
        value={selected.conversationTitle}
        onSave={text => { if (selected) dispatch(actions.requestSetConversationTitle(selected.conversationId, text)) }}
        icon={true}
      />
      <button
        className='saveTransciptButton'
        title='Save a Transcript of the Conversation'
        onClick={saveHtml}
      >
        <IconSave className='icon' />
      </button>
    </div>
    <div className='participants'>{selected.conversationViewOnly === false && <div className='icon' title='Add participant to conversation' onClick={() => {
      openSmartSearchDialogTeams('instantmessage',
        mySelf,
        ExtensionStatus.None,
        ExtensionSubStatus.Normal,
        mySelf.id,
        false,
        null,
        undefined,
        (action, type, entity, number) => {

          const endPointType = type === EntityType.Extension ? InstantMessagerParticipantEndpointScheme.IpfxExtension : type === EntityType.Contact ? InstantMessagerParticipantEndpointScheme.IpfxContact : InstantMessagerParticipantEndpointScheme.IpfxEmailContact
          const endPointServer = entity !== null ? entity.server : mySelf.server
          const endPointId = entity !== null ? entity.id : number

          dispatch(actions.requestImAddConversation(selected.conversationId, { endpointScheme: endPointType, endpointServer: endPointServer, endpointDeviceId: endPointId }))
        })
    }}><AddPeopleIcon /></div>} {Object.entries(selected.participants).map(([key, value]) => <ConversationParticipant conversation={selected} participant={value} key={'particpant' + key} />)}</div>
    <div ref={ref} className='messages' dangerouslySetInnerHTML={{ __html: html }} />
    <HtmlEditor className='editor' editorRef={editorRef} disabled={selected.conversationViewOnly === true} value={text} onChange={setText} onEnterKeyPress={sendMessage} onSendButtonClick={sendMessage} urgent={true} />
  </div>
}
const style = `
      <style>
        body {
          font-family: sans-serif;
          margin: 1rem;
        }
        .messages {
          display: flex;
          flex-direction: column;
          padding: 1rem;
          max-width: 70%;
          margin: auto;
          background: #ffffff;
          border-bottom: 1px solid #ccc;
        }
        .txt-item {
          display: grid;
          align-items: flex-start;
          max-width: 80%;
          column-gap: 0.5rem;
        }
        .txt-item + * {
          margin-top: 1rem;
        }
        .txt-item p {
          margin: 0;
          padding: 0;
        }
        .bubble {
          display: flex;
          padding: 0.25rem 0.5rem 0.5rem;
          border-radius: 3px 3px 0 0;
          overflow: hidden;
          font-size: 1.2rem;
          border: 1px solid #ccc;
          border-bottom: none;
        }
        .bubbleinnertext {
          flex: 1;
          display: block;
        }
        .sender {
          font-size: 0.9rem;
          padding: 0 0.5rem 0.25rem;
          border-radius: 0 0 3px 3px;
          border: 1px solid #ccc;
          border-top: none;
        }
        .left-item {
          margin-right: auto;
        }
        .left-item .bubble,
        .left-item .sender {
          background: #f0f0f0;
        }
        .right-item {
          margin-left: auto;
        }
        .right-item .bubble,
        .right-item .sender {
          background: rgba(0, 123, 255, 0.15);
        }
      </style>
    `;